<template>
    <div id="summary">
        <div class="app-container mt-5 mb-5">
            <div class="d-flex justify-content-between mb-5">
                <div class="d-flex align-center">
                    <v-skeleton-loader
                        v-if="providerLoading"
                        type="heading"
                        width="300"
                    ></v-skeleton-loader>
                    <template v-else>
                        <div class="category-btn mr-3" v-if="provider.last_not_drafted_version && provider.last_not_drafted_version.category">
                            {{ provider.last_not_drafted_version.category.label }}
                        </div>

                        <div class="page-title mr-3" v-if="provider.referent_data">
                            {{ provider.referent_data.name }}
                        </div>
                        <div class="page-title mr-3"
                             v-else-if="provider.last_not_drafted_version">
                            {{ provider.last_not_drafted_version.name }}
                        </div>

                        <div v-if="provider.apidae_source">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" :href="provider.apidae_source.url" target="_blank"
                                           class="shadow-none">
                                        <v-icon>open_in_new</v-icon>
                                    </v-btn>
                                </template>
                                <span>Voir sur Apidae</span>
                            </v-tooltip>
                        </div>
                    </template>
                </div>

                <div class="d-flex justify-content-between">
                    <div v-if="isCampaignAvailable('CMAJ') && provider.last_not_drafted_version && provider.last_not_drafted_version.category">
                        <v-btn outlined class="custom-button-primary" @click="displayConfirmBeforeModal">
                            <v-icon left>send</v-icon>
                            Envoyer une demande d'informations individuelle
                        </v-btn>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-6">
                    <div class="gap-15 d-flex flex-column">
                        <information-bloc v-if="account.tis_type.code !== 'NO_SIT'"
                                          :provider-id="providerId" :open-form-url="openFormUrl">
                        </information-bloc>
                        <div class="d-flex flex-row">
                            <contact-bloc :contact="contact" :providerId="providerId"
                                          :providerSelectionsCount="belongsToSelection" :provider="provider"
                                          :loading="providerLoading"
                                          class="col-6 p-0 mr-2 h-auto">
                            </contact-bloc>
                            <tag-bloc :provider-id="providerId" class="col-6 ml-2 h-auto"></tag-bloc>
                        </div>

                        <entity-bloc :provider-id="providerId" :entity="sitEntity"
                                     v-if="account.tis_type.code === 'DAHUB_QUEBEC' && account.id === 252">
                        </entity-bloc>
                        <note-bloc :provider-id="providerId"></note-bloc>
                        <selection-bloc :provider-id="providerId" v-if="account.tis_type.code === 'APIDAE'">
                        </selection-bloc>
                    </div>
                </div>
                <div class="col-6 gap-15 d-flex flex-column">
                    <campaign-bloc :provider-id="providerId"></campaign-bloc>
                    <comment-and-phone-bloc :provider-id="providerId"></comment-and-phone-bloc>
                    <history-bloc :actions="actions" :provider="provider" :loading="actionsLoading"></history-bloc>
                </div>
            </div>
        </div>

        <create-unique-form-modal ref="modale"></create-unique-form-modal>
        <confirm ref="confirm"></confirm>
    </div>
</template>

<script>
import { mapMutations, mapState } from "vuex"
import Confirm from "../../tools/Confirm"
import NoteBloc from "./NoteBloc"
import CommentAndPhoneBloc from "./CommentAndPhoneBloc"
import HistoryBloc from "./HistoryBloc"
import TagBloc from "./TagBloc"
import CampaignBloc from "./CampaignBloc"
import ContactBloc from "./ContactBloc"
import SelectionBloc from "./SelectionBloc.vue"
import InformationBloc from "./InformationBloc.vue"
import EntityBloc from "./EntityBloc.vue"

export default {
    name: "Summary",
    components: {
        ContactBloc,
        CampaignBloc,
        TagBloc,
        HistoryBloc,
        CommentAndPhoneBloc,
        NoteBloc,
        Confirm,
        CreateUniqueFormModal: () => import(/* webpackChunkName: "create-unique-form-modal" */ "../../campaign/cmaj/CreateUniqueFormModal"),
        SelectionBloc,
        InformationBloc,
        EntityBloc,
    },
    data() {
        return {
            providerId: null,
            providerLoading: true,
            actionsLoading: true,
            provider: {
                last_not_drafted_version: {
                    category: null,
                    name: "",
                },
                referent_data: null,
            },
            actions: [],
            contact: { profession: null },
            tabulations: [],
            belongsToSelection: false,
            openFormUrl: null,
            informationData: null,
            sitEntity: null,
        }
    },
    methods: {
        ...mapMutations("createCmaj", ["resetState", "setCurrentStep"]),
        getProvider() {
            this.providerLoading = true
            this.$http.get("auth/providers/" + this.providerId + "?skip_counts=1").then(res => {
                this.provider = res.body.data.provider
                this.contact = res.body.data.provider.contact
                this.belongsToSelection = res.body.data.belongsToSelection
                this.sitEntity = res.body.data.sitEntity
                this.providerLoading = false
            }).catch(() => {
                this.providerLoading = false
            })
        },
        getFields(categoryName) {
            this.$http.get("auth/account/referential/category-fields/" + categoryName).then(res => {
                this.tabulations = res.body.data.fields
            })
        },
        getActions() {
            this.actionsLoading = true
            this.$http.get("auth/providers/" + this.providerId + "/actions").then(res => {
                this.actions = res.body.data.actions
                this.actionsLoading = false
            }).catch(() => {
                this.actionsLoading = false
            })
        },
        setOpenModal() {
            this.$global.loading = true
            let categoryName = this.provider.last_not_drafted_version.category.label
            this.$http.get("auth/account/referential/category-fields/" + categoryName).then(res => {
                this.tabulations = res.body.data.fields
                this.setCurrentStep(1)

                this.$refs.modale.open(this.tabulations, this.contact, this.provider)
            })
            this.$global.loading = false
        },
        async displayConfirmBeforeModal() {
            this.$global.loading = true
            let cmajCount = 0
            let individualSollicitationsCount = 0
            try {
                let res = await this.$http.get("auth/providers/" + this.providerId + "/solicitation-counts")
                cmajCount = res.body.data.cmajCount || 0
                individualSollicitationsCount = res.body.data.individualSollicitationsCount || 0
            } catch (e) {
                // Silently handle error and proceed
            } finally {
                this.$global.loading = false
            }

            if (cmajCount > 0 || individualSollicitationsCount > 0) {
                let message = '<strong>Une ou des sollicitations sont déjà en cours.</strong> <br>'
                if (cmajCount > 0 && individualSollicitationsCount === 0) {
                    message += 'Ce prestataire est déjà présent dans <strong>' + cmajCount + '</strong> campagne(s).<br>'
                } else if (individualSollicitationsCount > 0 && cmajCount === 0) {
                    message += 'Ce prestataire a déjà reçu <strong>' + individualSollicitationsCount +
                      '</strong> demande(s) individuelle(s).<br>'
                } else if (individualSollicitationsCount > 0 && cmajCount > 0) {
                    message += 'Ce prestataire est déjà présent dans <strong>' + cmajCount + '</strong> campagne(s) en cours' +
                      ' et a déjà reçu <strong>' + individualSollicitationsCount + '</strong> demande(s) individuelle(s).<br>'
                }

                message += '<br>' + 'Êtes-vous sûr de vouloir envoyer une nouvelle demande individuelle ?'

                this.$refs.confirm.open('Prestataire déjà sollicité',
                  message + '<br>',
                  { confirmColor: 'primary', cancelColor: '#0b697e' }).then((confirm) => {
                    if (confirm) {
                        this.setOpenModal()
                    }
                })
            } else {
                this.setOpenModal()
            }
        },
        isCampaignAvailable(code) {
            let available = false
            let contracts = this.account.current_contracts

            if (contracts.length) {
                contracts.forEach(contract => {
                    let module = contract.contract_offer.options.filter(option => option.code === code)

                    if (module.length) {
                        available = true
                    }
                })
            }
            return available
        },
    },
    computed: {
        ...mapState("account", ["user", "account"]),
    },
    created() {
        let hash = this.$route.params.hash
        this.providerId = parseInt(atob(hash))

        this.getProvider()
        this.getActions()

        this.$global.$on("reload-summary", () => {
            this.getProvider()
            this.getActions()

            if (this.$refs.modale && this.$refs.modale.cancel) {
                this.$refs.modale.cancel()
            }
        })

        this.openFormUrl = "/sit/formulaire/" + this.providerId
    },
    beforeDestroy() {
        this.$global.$off("reload-summary")
    },
}
</script>